import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, Input, OnChanges, OnInit, Signal, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LimitedCurrencyAmount } from '@lx/currency/shared/model/currency.model';
import { TripDates } from '@lx/quote-tool/shared/types';
import { Accommodation as AccommodationAPI, GroupableAccommodation } from '@lx/shared/data-access/products';
import { Amount } from '@lx/shared/data-access/types';
import { ButtonComponent } from '@lx/shared/ui/button';
import { LoadingService } from '@lx/shared/ui/loading-spinner';
import { combineLatest, Observable, of, take } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { GoogleAnalyticsEventEnum } from '../../google-analytics';
import { StudyPeriodPipe } from '@lx/quote-tool/lite/shared/ui/pipes';
import { AccommodationsService } from '../../services';
import { QTLiteStoreSignalsAdapterService } from '../../services/qt-lite-store-signals-adapter.service';
import { QuoteStore, QuoteToolLiteStore } from '../../store';
import { Accommodation, ACCOMMODATIONS_FALLBACK_IMAGE } from '../../types';
import { AccommodationDetailsComponent } from '../accommodation-details/accommodation-details.component';
import { AccommodationOptionsComponent } from '../accommodation-options/accommodation-options.component';
import { AccommodationsComponent } from '../accommodations/accommodations.component';
import { CardContainerComponent, ImageComponent } from '@lx/quote-tool/lite/shared/ui/components';
import { SidePanelGalleryComponent } from '@lx/quote-tool/lite/shared/ui/components';
import { SidePanelComponent } from '@lx/quote-tool/lite/shared/ui/components';
import { AccommodationWithPriceAndDiscount } from './accommodation-with-price-and-discount.type';
import { OperationKey } from '../../constants';
import { MessageComponent } from '@lx/quote-tool/lite/shared/ui/components';
import { SidePanelService } from '@lx/quote-tool/lite/shared/services';

@Component({
  standalone: true,
  selector: 'lx-accommodation',
  templateUrl: './accommodation.component.html',
  styleUrls: ['./accommodation.component.scss'],
  imports: [
    CardContainerComponent,
    SidePanelComponent,
    AccommodationsComponent,
    CommonModule,
    StudyPeriodPipe,
    AccommodationDetailsComponent,
    ButtonComponent,
    AccommodationOptionsComponent,
    SidePanelGalleryComponent,
    ImageComponent,
    MessageComponent,
    MessageComponent,
  ],
})
export class AccommodationComponent implements OnInit, OnChanges {
  @Input() accommodations: AccommodationAPI[] | undefined;
  @Input() accommodationOptional: boolean | undefined;
  @Input() moreAccommodationsDisclaimerShown: boolean | undefined;

  accommodationsDialogVisible = false;
  accommodationDetailsDialogVisible = false;
  endPrice: LimitedCurrencyAmount[] | Amount[] | undefined;

  selectedAccommodationWithQuotedPrice$: Observable<AccommodationWithPriceAndDiscount> | undefined;

  tripDates$: Observable<TripDates> | undefined;

  compactedAccommodations: GroupableAccommodation[] = [];
  currency: Signal<string | undefined>;

  selectedAccommodation: Accommodation | undefined;

  dataLayerEventName$: Observable<GoogleAnalyticsEventEnum> | undefined;

  protected readonly ACCOMMODATIONS_FALLBACK_IMAGE = ACCOMMODATIONS_FALLBACK_IMAGE;

  protected loading: Signal<boolean | undefined>;

  private readonly quoteToolLiteStore = inject(QuoteToolLiteStore);
  private readonly qtLiteStoreSignalsAdapterService = inject(QTLiteStoreSignalsAdapterService);
  private readonly quoteStore = inject(QuoteStore);
  private readonly accommodationsService = inject(AccommodationsService);
  private readonly sidePanelService = inject(SidePanelService);
  private readonly loadingService = inject(LoadingService);

  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.tripDates$ = this.quoteToolLiteStore.tripDates$;

    this.initDataLayerEventName();
    this.currency = this.qtLiteStoreSignalsAdapterService.selectedCurrency;
    this.loading = this.loadingService.areAllOperationsLoading([OperationKey.prices, OperationKey.extras]);
  }

  ngOnChanges({ accommodations }: SimpleChanges): void {
    if (accommodations?.currentValue && accommodations?.currentValue.length) {
      this.quoteToolLiteStore.selectedAccommodation$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe((selectedAccommodation) => {
        this.compactedAccommodations = this.accommodationsService.getCompactedAndSortedAccommodations(accommodations.currentValue, selectedAccommodation);
        this.selectedAccommodationWithQuotedPrice$ = this.getSelectedAccommodationWithQuotedAccommodationData();
      });
    }
  }

  updateSelectedAccommodation(accommodation: Accommodation) {
    this.selectedAccommodation = accommodation;
  }

  saveSelectedAccommodation(): void {
    this.hideAccommodationsDialog(() => {
      const { code, type } = this.selectedAccommodation;
      this.quoteToolLiteStore.updateState({
        selectedAccommodation: {
          code,
          type,
        },
      });
    });
  }

  openAccommodationsSidePanel() {
    this.accommodationsDialogVisible = true;
  }

  hideAccommodationsDialog(callback?: () => void) {
    this.sidePanelService.hidePanel(() => {
      if (callback) {
        callback();
      }
      this.accommodationsDialogVisible = false;
    });
  }

  openAccommodationDetailsSidePanel() {
    this.accommodationDetailsDialogVisible = true;
  }

  hideAccommodationDetailsDialog() {
    this.sidePanelService.hidePanel(() => (this.accommodationDetailsDialogVisible = false));
  }

  private getSelectedAccommodationWithQuotedAccommodationData() {
    return this.quoteToolLiteStore.selectedAccommodation$.pipe(
      switchMap((selectedAccommodation) => combineLatest([of(selectedAccommodation), this.quoteStore.accommodation$])),
      map(([selectedAccommodation, quotedAccommodation]) => {
        const accommodation = this.compactedAccommodations.find((accommodation) => accommodation.code === selectedAccommodation?.code);
        if (!accommodation) {
          return {
            ...selectedAccommodation,
            endPrice: this.getCheapestPrices(this.accommodations),
          } as AccommodationWithPriceAndDiscount;
        }

        return {
          type: selectedAccommodation.type,
          code: selectedAccommodation.code,
          title: accommodation?.name,
          description: accommodation?.description,
          mealOptions: accommodation?.mealOptions,
          bathroomType: accommodation?.bathroomType,
          roomType: accommodation?.roomType,
          galleryUrls: accommodation?.galleryUrls,
          withPromotion: accommodation?.hasPromotion,
          price: quotedAccommodation?.price,
          endPrice: quotedAccommodation?.endPrice,
          discountTotalAsPercentage: quotedAccommodation?.discountTotalAsPercentage,
          units: quotedAccommodation?.units,
          groupingKey: accommodation?.groupingKey,
          groupedItems: accommodation?.groupedItems,
        };
      }),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  private getCheapestPrices(accommodations: Accommodation[]): Amount[] {
    if (!accommodations || accommodations.length === 0) {
      return null;
    }

    const accommodationIndex = accommodations.reduce((cheapestIndex, accommodation, index, array) => {
      return accommodation.price?.value < array[cheapestIndex].price?.value ? index : cheapestIndex;
    }, 0);

    return accommodations[accommodationIndex].multiCurrencyPrice;
  }

  /**
   * Init the observable for data layer event name based on the selected accommodation type (add -> when OTHER, edit in other case)
   */
  private initDataLayerEventName() {
    this.dataLayerEventName$ = this.quoteToolLiteStore.selectedAccommodation$.pipe(
      map((selectedAccommodation) =>
        selectedAccommodation?.code ? GoogleAnalyticsEventEnum.QuoteAccommodationEdit : GoogleAnalyticsEventEnum.QuoteAccommodationAdd,
      ),
    );
  }
}
